import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddPartyAccountForm, { PartyAccountFormData } from './AddPartyAccountForm';
import { 
  getPartyAccountListAPI, 
  savePartyAccountAPI, 
  deletePartyAccountAPI,
  getPartyListAPI,
  PartyAccount, 
  PartyAccountList,
  Party
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './PartyAccounts.module.css';

const PartyAccounts: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [partyFilter, setPartyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accounts, setAccounts] = useState<PartyAccountList[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState<PartyAccount | null>(null);
  const [parties, setParties] = useState<Array<{ id: string; name: string }>>([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const permissions = useModulePermission('Party Accounts');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [partyFilter, typeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: any = { pageNo: 1, pageSize: 100 };
      
      // Add partyId - send 0 if 'all', otherwise send the selected ID
      searchRequest.partyId = partyFilter === 'all' ? 0 : parseInt(partyFilter);
      
      // Add accountTypeId - send 0 if 'all', otherwise send the selected ID
      searchRequest.accountTypeId = typeFilter === 'all' ? 0 : parseInt(typeFilter);
      
      const [accountsResponse, partiesResponse] = await Promise.all([
        getPartyAccountListAPI(searchRequest),
        getPartyListAPI({ pageNo: 1, pageSize: 100 }),
      ]);
      setAccounts(accountsResponse.data || []);
      setParties((partiesResponse.data || []).map(p => ({ id: p.id?.toString() || '', name: p.partyName })));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  const handleSubmitAccount = async (accountData: PartyAccountFormData) => {
    try {
      const isActive = accountData.status === 'active';
      const partyId = parseInt(accountData.party);
      const accountTypeId = parseInt(accountData.accountType);
      const qrTypeId = parseInt(accountData.qrType);
      const networkId = parseInt(accountData.network);

      if (editingAccount) {
        await savePartyAccountAPI({
          id: editingAccount.id,
          partyId: partyId,
          accountTypeId: accountTypeId,
          bankName: accountData.bankName,
          ifscCode: accountData.ifscCode,
          holderName: accountData.holderName,
          accountNo: accountData.accountNo,
          branchName: accountData.branch || '',
          notes: accountData.notes || '',
          upI_ID: accountData.upI_ID || '',
          linkBankName: accountData.linkBankName || '',
          qrLabel: accountData.qrLabel || '',
          qrTypeId: qrTypeId,
          qrImageUrl: accountData.qrImageUrl || '',
          walletAddress: accountData.walletAddress || '',
          walletLabel: accountData.walletLabel || '',
          networkId: networkId,
          isActive: isActive,
        });
        showSuccess('Party account updated successfully!');
      } else {
        await savePartyAccountAPI({
          partyId: partyId,
          accountTypeId: accountTypeId,
          bankName: accountData.bankName,
          ifscCode: accountData.ifscCode,
          holderName: accountData.holderName,
          accountNo: accountData.accountNo,
          branchName: accountData.branch || '',
          notes: accountData.notes || '',
          upI_ID: accountData.upI_ID || '',
          linkBankName: accountData.linkBankName || '',
          qrLabel: accountData.qrLabel || '',
          qrTypeId: qrTypeId,
          qrImageUrl: accountData.qrImageUrl || '',
          walletAddress: accountData.walletAddress || '',
          walletLabel: accountData.walletLabel || '',
          networkId: networkId,
          isActive: isActive,
        });
        showSuccess('Party account created successfully!');
      }
      await loadData();
      setIsModalOpen(false);
      setEditingAccount(null);
    } catch (error) {
      console.error('Failed to save party account:', error);
      showError('Failed to save party account. Please try again.');
    }
  };

  const handleView = (account: PartyAccount) => {
    alert(`Party Account Details:\n\nParty ID: ${account.partyId}\nType: ${account.accountTypeId}\nBank: ${account.bankName || 'N/A'}\nAccount No: ${account.accountNo || 'N/A'}\nStatus: ${account.isActive ? 'Active' : 'Inactive'}`);
  };

  const handleEdit = (account: PartyAccount) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleDelete = async (account: PartyAccount) => {
    if (window.confirm(`Are you sure you want to delete this account?`)) {
      try {
        const response = await deletePartyAccountAPI(account.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('Party account deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete party account');
        }
      } catch (error) {
        console.error('Failed to delete party account:', error);
        showError('Failed to delete party account. Please try again.');
      }
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter((account) => {
    const matchesSearch =
      account.accountNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.bankName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesParty = partyFilter === 'all' || account.partyId.toString() === partyFilter;
    const matchesType = typeFilter === 'all' || account.accountTypeId.toString() === typeFilter.toLowerCase();
    
    return matchesSearch && matchesParty && matchesType;
  });

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Party Account Details</h1>
          <p className={styles.subtitle}>Manage party payment accounts (Bank, UPI, QR, Wallet)</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddAccount}>
            + Add Party Account
          </button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className={styles.searchFilterBar}>
        <div className={styles.searchInputWrapper}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by party name or account..."
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className={styles.filter}
          value={partyFilter}
          onChange={(e) => setPartyFilter(e.target.value)}
        >
          <option value="all">All Parties</option>
          {parties.map(party => (
            <option key={party.id} value={party.id}>{party.name}</option>
          ))}
        </select>
        <select
          className={styles.filter}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="bankaccount">Bank Account</option>
          <option value="upi">UPI</option>
          <option value="qrcode">QR Code</option>
          <option value="wallet">Wallet</option>
          <option value="walletaddress">Wallet Address</option>
        </select>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading party accounts...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>PARTY</th>
                <th>TYPE</th>
                <th>ACCOUNT DETAILS</th>
                <th>NOTES</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 6 : 5} className={styles.emptyState}>No party accounts found</td>
                </tr>
              ) : (
                filteredAccounts.map((account) => (
                  <tr key={account.id}>
                    <td>{account.partyName || account.partyId}</td>
                    <td>
                      <span className={`${styles.typeBadge} ${styles[account.accountType?.toLowerCase().replace(' ', '') || '']}`}>
                        {account.accountType || account.accountTypeId}
                      </span>
                    </td>
                    <td>
                      {/* Bank Account - Show Bank Name, Account No, Holder Name */}
                      {account.accountTypeId === 1 && (
                        <>
                          {account.bankName && <div><strong>{account.bankName}</strong></div>}
                          {account.accountNo && <div>{account.accountNo}</div>}
                          {account.holderName && <div className={styles.holderName}>{account.holderName}</div>}
                        </>
                      )}
                      
                      {/* UPI - Show UPI ID */}
                      {account.accountTypeId === 2 && (
                        <div><strong>{account.upI_ID || 'N/A'}</strong></div>
                      )}
                      
                      {/* QR Code - Show QR Label */}
                      {account.accountTypeId === 3 && (
                        <div><strong>{account.qrLabel || 'N/A'}</strong></div>
                      )}
                      
                      {/* Wallet / Wallet Address - Show Wallet Label */}
                      {(account.accountTypeId === 4 || account.accountTypeId === 5) && (
                        <div><strong>{account.walletLabel || 'N/A'}</strong></div>
                      )}
                    </td>
                    <td>{account.notes}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[account.isActive ? 'active' : 'inactive']}`}>
                        {account.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(account)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(account)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Party Account Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingAccount ? 'Edit Party Account' : 'Add New Party Account'}
      >
        <AddPartyAccountForm 
          onSubmit={handleSubmitAccount} 
          onCancel={handleCloseModal}
          parties={parties}
          initialData={editingAccount ? {
            party: editingAccount.partyId.toString(),
            accountType: editingAccount.accountTypeId.toString(),
            bankName: editingAccount.bankName || '',
            holderName: editingAccount.holderName || '',
            accountNo: editingAccount.accountNo || '',
            ifscCode: editingAccount.ifscCode || '',
            branch: editingAccount.branchName || '',
            status: editingAccount.isActive ? 'active' : 'inactive',
            notes: editingAccount.notes || '',
            upI_ID: editingAccount.upI_ID || '',
            linkBankName: editingAccount.linkBankName || '',
            qrLabel: editingAccount.qrLabel || '',
            qrType: editingAccount.qrTypeId ? editingAccount.qrTypeId.toString() : '',
            qrImageUrl: editingAccount.qrImageUrl || '',
            walletAddress: editingAccount.walletAddress || '',
            walletLabel: editingAccount.walletLabel || '',
            network: editingAccount.networkId ? editingAccount.networkId.toString() : '',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default PartyAccounts;
